import type { InvoiceTransaction, Transaction } from "@/apis/gql/types";
import { SignedAmount } from "@/components/shared/miscellaneous/signed-amount";
import TextWithTooltip from "@/components/shared/tooltips/text-with-tooltip";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  TypographyCaption,
  TypographyMuted,
  TypographySmall,
} from "@/components/ui/typography";
import { InvoiceType } from "@/lib/constants/invoiceStatus";
import { sumJournalEntries } from "@/lib/ledger/journal-entry-utils";
import { cn } from "@/lib/utils";
import { useDeleteInvoiceFromTransaction } from "@/queries/transactions/delete-invoice";
import { useParams, useSearch } from "@tanstack/react-router";
import { format } from "date-fns";
import { FileText, Link2, X } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { InvoiceAmountBadge } from "../../../shared/invoice-amount-badge";

export const InvoiceSuggestionCell = ({
  transaction,
  onMarkAsReviewed,
}: {
  transaction: Transaction;
  onMarkAsReviewed: () => Promise<void>;
}) => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col w-full">
      {transaction.invoices.map((invoice) => (
        <InvoiceSuggestionCard
          key={invoice.invoiceId}
          transaction={transaction}
          invoice={invoice as InvoiceTransaction}
          onMarkAsReviewed={onMarkAsReviewed}
          showApproveButton={transaction.invoices.length === 1}
        />
      ))}
      {transaction.invoices.length > 1 && (
        <Button
          variant="outline"
          className="h-8 gap-2 w-fit self-end my-2 mx-2"
          onClick={() => {
            onMarkAsReviewed();
          }}
        >
          <Link2 className="w-4 h-4" />
          {t("transactions.actions.match_all")}
        </Button>
      )}
    </div>
  );
};

type Props = {
  invoice: InvoiceTransaction;
  transaction: Transaction;
  onMarkAsReviewed: () => Promise<void>;
  showApproveButton: boolean;
};

const InvoiceSuggestionCard = ({
  invoice,
  transaction,
  onMarkAsReviewed,
  showApproveButton,
}: Props) => {
  const { t } = useTranslation();

  const [deleteInvoiceFromTransaction] = useDeleteInvoiceFromTransaction();
  const { company } = useParams({ strict: false });
  const search = useSearch({ strict: false });
  const view = (search as { view?: string }).view;

  const invoiceData = invoice;

  const { debit: totalAmount } = sumJournalEntries(invoice.journalEntries);

  const invoiceTotalAmount = useMemo(() => {
    if (invoice.invoiceType === InvoiceType.RECEIVABLE) {
      return totalAmount.toNumber();
    }
    return totalAmount.toNumber() * -1;
  }, [totalAmount, invoice.invoiceType]);

  const handleDeleteInvoice = () => {
    deleteInvoiceFromTransaction({
      bankTxnIds: [transaction.bankTxnId],
      invoiceIds: [invoice.invoiceId],
    });
  };

  const prefetchInvoice = () => {
    // Prefetching is handled by TanStack Router automatically via Link preload
  };

  const isNegative = invoiceTotalAmount < 0;

  const currency = "EUR"; // we default to EUR for the currency

  return (
    <div className="group flex items-center w-full px-2 py-1">
      <div
        className={cn(
          "flex items-center w-full border rounded-md cursor-pointer py-2 px-3 min-h-[44px] bg-white shadow-md",
        )}
        style={{ width: "100%", minWidth: 0, overflow: "hidden" }}
        onMouseEnter={prefetchInvoice}
      >
        <div className="h-8 w-8 mr-2 flex-shrink-0 rounded-lg border border-border bg-secondary/50 transition-colors flex items-center justify-center">
          <FileText className="h-3.5 w-3.5 text-muted-foreground" />
        </div>
        <div className="flex flex-col flex-1 min-w-0 py-0.5">
          {invoiceData.invoiceDate && (
            <div className="flex flex-row items-center gap-1">
              <TextWithTooltip
                text={format(invoiceData.invoiceDate, "dd.MM.yyyy")}
              >
                <TypographyCaption className="text-xs text-muted-foreground truncate">
                  {format(invoiceData.invoiceDate, "dd.MM.yyyy")}
                </TypographyCaption>
              </TextWithTooltip>
            </div>
          )}
          <TextWithTooltip text={invoiceData.supplierName ?? t("unknown")}>
            <TypographySmall className="text-sm truncate">
              {invoiceData.supplierName ?? t("unknown")}
            </TypographySmall>
          </TextWithTooltip>
          <TextWithTooltip text={invoiceData.invoiceNo}>
            <TypographyMuted className="text-xs text-muted-foreground truncate">
              {invoiceData.invoiceNo}
            </TypographyMuted>
          </TextWithTooltip>
        </div>
        <div className="flex items-center justify-end px-2 min-w-[80px] flex-col gap-1">
          <SignedAmount
            amount={Math.abs(invoiceTotalAmount)}
            currency={currency}
            side={isNegative ? "debit" : "credit"}
          />
          <InvoiceAmountBadge
            transaction={transaction}
            invoiceTotalAmount={invoiceTotalAmount}
            currency={currency}
            originalCurrency={invoice.currency}
            originalAmount={invoice.totalAmount}
          />
        </div>
        <div className="flex flex-row gap-1 ml-2">
          <div className="flex items-center justify-center">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-8 w-8"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteInvoice();
                    }}
                    tabIndex={-1}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="left" align="center">
                  {t("transactions.suggestions.remove_attachment")}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          {showApproveButton && (
            <div className="flex items-center justify-center">
              <Button
                variant="outline"
                size="sm"
                className="h-8 gap-1"
                onClick={(e) => {
                  e.stopPropagation();
                  onMarkAsReviewed();
                }}
                tabIndex={-1}
              >
                <Link2 className="w-3.5 h-3.5" />
                Zuordnen
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
